import {
  addDoc,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  getDoc,
  query,
  orderBy,
} from "firebase/firestore";
import { createTimestamp, standardizeTimestamps } from "@/utils/dateUtils";
import { appointmentsCol, requestsCol, getCurrentUser, validateAppointmentData } from "./utils";
import type { 
  CreateAppointmentInput, 
  Appointment, 
  AppointmentRequest,
  AppointmentUpdateData 
} from "./types";

const isVisibleAppointment = (appointment: Appointment) =>
  appointment.excludedFromAdminMetrics !== true;

/**
 * Create a new appointment
 */
export const createAppointment = async (payload: CreateAppointmentInput): Promise<string> => {
  const user = getCurrentUser();
  
  // Validate input data
  const validationErrors = validateAppointmentData(payload);
  if (validationErrors.length > 0) {
    throw new Error(`Validation failed: ${validationErrors.join(", ")}`);
  }

  // Check for time slot conflicts
  const existingAppointments = await listAppointments();
  const conflicts = existingAppointments.filter(apt => 
    apt.date === payload.date && 
    apt.time === payload.time &&
    apt.status !== "cancelled" &&
    apt.status !== "no-show"
  );

  if (conflicts.length > 0) {
    throw new Error("Time slot is already booked");
  }

  const appointmentData = {
    ...payload,
    status: "scheduled",
    createdAt: createTimestamp(),
    updatedAt: createTimestamp(),
    lawyerId: user.uid,
    notes: [],
    actions: [],
  };

  const docRef = await addDoc(appointmentsCol(user.uid), appointmentData);
  return docRef.id;
};

/**
 * Get all appointments for the current user
 */
export const listAppointments = async (): Promise<Appointment[]> => {
  const user = getCurrentUser();
  
  const q = query(appointmentsCol(user.uid), orderBy("date", "desc"));
  const snapshot = await getDocs(q);
  
  const appointments = snapshot.docs.map((doc) => ({
    id: doc.id,
    ...standardizeTimestamps(doc.data()),
  })) as Appointment[];

  return appointments.filter(isVisibleAppointment);
};

/**
 * Get a specific appointment by ID
 */
export const getAppointment = async (id: string): Promise<Appointment | null> => {
  const user = getCurrentUser();
  
  const docRef = doc(appointmentsCol(user.uid), id);
  const docSnap = await getDoc(docRef);
  
  if (!docSnap.exists()) {
    return null;
  }
  
  return {
    id: docSnap.id,
    ...standardizeTimestamps(docSnap.data()),
  } as Appointment;
};

/**
 * Update an existing appointment
 */
export const updateAppointment = async (
  id: string, 
  updates: AppointmentUpdateData
): Promise<void> => {
  const user = getCurrentUser();
  
  const updateData = {
    ...updates,
    updatedAt: createTimestamp(),
  };
  
  const docRef = doc(appointmentsCol(user.uid), id);
  await updateDoc(docRef, updateData);
};

/**
 * Delete an appointment
 */
export const deleteAppointment = async (id: string): Promise<void> => {
  const user = getCurrentUser();
  
  const docRef = doc(appointmentsCol(user.uid), id);
  await deleteDoc(docRef);
};

/**
 * Create a new appointment request
 */
export const createAppointmentRequest = async (
  payload: Omit<AppointmentRequest, "id" | "createdAt" | "updatedAt">
): Promise<string> => {
  const user = getCurrentUser();
  
  const requestData = {
    ...payload,
    status: "pending",
    createdAt: createTimestamp(),
    updatedAt: createTimestamp(),
    lawyerId: user.uid,
  };
  
  const docRef = await addDoc(requestsCol(user.uid), requestData);
  return docRef.id;
};

/**
 * Get all appointment requests for the current user
 */
export const listAppointmentRequests = async (): Promise<AppointmentRequest[]> => {
  const user = getCurrentUser();
  
  const q = query(requestsCol(user.uid), orderBy("createdAt", "desc"));
  const snapshot = await getDocs(q);
  
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...standardizeTimestamps(doc.data()),
  })) as AppointmentRequest[];
};

/**
 * Update appointment request status
 */
export const updateAppointmentRequestStatus = async (
  id: string,
  status: string,
  reason?: string
): Promise<void> => {
  const user = getCurrentUser();
  
  const updateData: any = {
    status,
    updatedAt: createTimestamp(),
  };
  
  if (reason) {
    updateData.reason = reason;
  }
  
  const docRef = doc(requestsCol(user.uid), id);
  await updateDoc(docRef, updateData);
};

/**
 * Delete an appointment request
 */
export const deleteAppointmentRequest = async (id: string): Promise<void> => {
  const user = getCurrentUser();
  
  const docRef = doc(requestsCol(user.uid), id);
  await deleteDoc(docRef);
};
